/**
 * V70 Tournament — 디자인 토큰 (팔레트/타이포/그림자).
 *
 * 사용자 지시 (2026-10-19):
 *   - Marble: 프리미엄 보드게임 (딥 네이비, 샌드, 세이지, 블루그레이)
 *   - AcidRain: 미니멀 프리미엄 아케이드 (차콜, 블루그레이, 샴페인 골드, 밝은 텍스트)
 *   - 공통: 색상 수 최소화, 불필요한 그라데이션/글로우/강한 그림자 축소
 *
 * 사용 지침:
 *   - 각 game 컴포넌트는 아래 상수를 `import` 하여 inline style 또는 CSS variable 로 적용
 *   - 과도한 네온/radial glow 는 축소 (0 0 24px → 0 2px 4px / 보조 색 soft shadow)
 *   - 애니메이션 타이밍/transform 은 변경 금지 (사용자 지시 §변경 금지)
 */

/* ──────────────────────────────────────────────────────────
 *  마블 — 프리미엄 보드게임
 * ────────────────────────────────────────────────────────── */
export const MARBLE_COLORS = {
  // 메인 — 딥 네이비 (배경, 주요 패널)
  mainDark: '#202D3B',
  mainDarker: '#17222E',
  mainLighter: '#2C3B4D',

  // 보조1 — 샌드 (강조/하이라이트, 선택 상태, 보드 보상 셀)
  sand: '#D8C9AB',
  sandSoft: '#E8DFC8',
  sandDeep: '#B8A684',

  // 보조2 — 세이지 (중립 액센트, 상점/safe 셀)
  sage: '#748A80',
  sageSoft: '#8E9F97',
  sageDeep: '#5C7268',

  // 중립 — 블루그레이 (텍스트/보더/바탕)
  neutral: '#AAB6BE',
  neutralSoft: '#C4CDD3',
  neutralDeep: '#8894A0',

  // 텍스트
  textPrimary: '#F0F2F5',      // 어두운 배경 위 밝은 텍스트
  textSecondary: '#AAB6BE',
  textOnLight: '#202D3B',      // 밝은 배경 위 어두운 텍스트
  textOnLightMuted: '#5C7268',

  // 상태 색상 — 톤 다운 (원색 X)
  success: '#5A7E5A',          // 긍정 (득점) — 세이지 쪽 톤
  warning: '#A68C5B',          // 경고 — 샌드 쪽 톤
  danger: '#8B4A4A',           // 음수/위험 — 억제된 적
  info: '#5C7A8C',             // 알림 — 블루그레이 톤

  // 그림자 (soft, radial glow 금지)
  shadowSoft: '0 2px 4px rgba(32, 45, 59, 0.15)',
  shadowMedium: '0 4px 8px rgba(32, 45, 59, 0.2)',
  shadowStrong: '0 6px 16px rgba(32, 45, 59, 0.3)',

  // 보더
  borderSoft: 'rgba(170, 182, 190, 0.25)',
  borderStrong: '#5C7268',
} as const;

/* ──────────────────────────────────────────────────────────
 *  타자왕 (산성비) — NGLE 브랜드 다크 테마 (2026-10-19)
 *  마블의 NGLE 브랜드 Teal 과 통일하되 다크모드로 디자인.
 *  배경: 짙은 네이비/차콜 (#111D25 계열)
 *  패널: 밝기 다른 다크 Teal (#1C3039 계열)
 *  Primary Teal: #00A6B5, Light Teal: #9CDDE2, Accent Gold: #F5C842
 * ────────────────────────────────────────────────────────── */
export const ACIDRAIN_COLORS = {
  // 메인 — 짙은 네이비 (지시 §2 "#111D25 계열")
  bgPrimary: '#111D25',
  bgSecondary: '#162530',

  // 보조 — 다크 Teal 패널 (지시 §2 "#1C3039 계열")
  panel: '#1C3039',
  panelSoft: '#243E4A',
  panelDeep: '#152630',

  // 포인트 — Accent Gold (지시 §2 "#F5C842")
  gold: '#F5C842',
  goldSoft: '#FFE07A',
  goldDeep: '#C99C2A',

  // 브랜드 Teal — 지시 §2 신규 추가
  primaryTeal: '#00A6B5',
  lightTeal: '#9CDDE2',
  deepTeal: '#106070',

  // 텍스트 (다크 배경 위 밝은 톤 — 가독성 WCAG AA)
  text: '#DFF2F4',             // 메인 텍스트 (단어, 입력, 점수) — Mist Teal
  textMuted: '#8CA3AB',
  textOnGold: '#111D25',       // Gold 배경 위 어두운 텍스트

  // 상태 (톤 다운, 색상+아이콘 조합으로 색상만 의존 X — 지시 §2 "색상만으로 구별하지 않도록")
  success: '#9CDDE2',          // 정답/득점 — Light Teal (브랜드 통일)
  danger: '#F5A5A5',           // 생명력 감소 — 소프트 레드 (다크 배경 가독성)
  warning: '#F5C842',          // 경고 — Gold 재사용
  info: '#9CDDE2',

  // 난이도별 단어 색상 (톤 다운, 다크 배경 가독성)
  //   easy: Mist Teal (일반) / medium: Gold (주의) / hard: 소프트 레드 (위험)
  //   색상 외에 fontSize/weight 로도 구별 가능 (기존 유지)
  wordEasy: '#DFF2F4',
  wordMedium: '#F5C842',
  wordHard: '#F5A5A5',

  // 그림자/글로우 (다크 테마 — 깊이감)
  shadowSoft: '0 1px 3px rgba(0, 0, 0, 0.5)',
  shadowMedium: '0 3px 8px rgba(0, 0, 0, 0.55)',

  // 보더
  borderSoft: 'rgba(0, 166, 181, 0.25)',    // Primary Teal 투명
  borderStrong: '#2A4552',                   // Dark Teal 보더
} as const;

/* ──────────────────────────────────────────────────────────
 *  공통 토큰 — Tournament 전역
 * ────────────────────────────────────────────────────────── */
export const SHARED_TOURNAMENT = {
  // 폰트 가중치
  fontBold: 700,
  fontBlack: 900,
  fontRegular: 500,
  // 라운드
  radiusSm: 4,
  radiusMd: 6,
  radiusLg: 8,
  radiusFull: 999,
  // 간격
  gapSm: 4,
  gapMd: 8,
  gapLg: 12,
  // 애니메이션 ease
  easeOut: 'cubic-bezier(0.2, 0.8, 0.3, 1)',
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

export type MarbleColorKey = keyof typeof MARBLE_COLORS;
export type AcidRainColorKey = keyof typeof ACIDRAIN_COLORS;
